import {
  CSSProperties,
  MouseEvent,
  TouchEvent,
  useEffect,
  useRef,
} from 'react';
import { tv } from 'tailwind-variants';

import { ValueInfo } from './ValueInfo';
import { THUMB_WIDTH } from './types';
import { useHandleDrag } from './useHandleDrag';

const border = tv({
  base: 'border-[3px]',
  variants: {
    color: {
      gray: 'border-gray-30 dark:border-gray-75',
      violet: 'border-violet dark:border-violet',
    },
  },
  compoundVariants: [],
  defaultVariants: {
    color: 'gray',
  },
});

export const Range = ({
  value,
  minValue,
  maxValue,
  onChange,
  handleMouseDown,
  color,
  style,
  hideThumb,
  format,
}: {
  value: number;
  minValue: number;
  maxValue: number;
  onChange: (value: number) => void;
  handleMouseDown: (side: 'range') => (e: MouseEvent | TouchEvent) => void;
  style: CSSProperties;
  color?: 'gray' | 'violet';
  hideThumb?: boolean;
  format?: (value: number) => string;
}) => {
  const rangeRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLDivElement>(null);
  const mouseDownRef = useRef<{ x: number; time: number } | null>(null);
  const CLICK_THRESHOLD = 5; // pixels
  const CLICK_TIME_THRESHOLD = 200; // milliseconds

  const getThumbPosition = (value: number) => {
    return ((value - minValue) / (maxValue - minValue)) * 100;
  };

  const {
    isDragging: isDraggingThumb,
    handleMouseDown: handleThumbMouseDown,
    handleTouchStart: handleThumbTouchStart,
  } = useHandleDrag({
    containerRef: rangeRef,
    onDrag: (position) => {
      const newValue = minValue + (position / 100) * (maxValue - minValue);
      const clampedValue = Math.min(Math.max(newValue, minValue), maxValue);
      onChange(clampedValue);
    },
  });

  const handleRangeDown = (e: MouseEvent | TouchEvent) => {
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    mouseDownRef.current = { x: clientX, time: Date.now() };

    const handleMouseMove = ((moveEvent: MouseEvent | TouchEvent) => {
      if (!mouseDownRef.current) return;

      const moveClientX =
        'touches' in moveEvent
          ? moveEvent.touches[0].clientX
          : moveEvent.clientX;
      const deltaX = Math.abs(moveClientX - mouseDownRef.current.x);

      if (deltaX >= CLICK_THRESHOLD) {
        // If we've moved beyond the threshold, it's a drag
        handleMouseDown('range')(e);

        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('touchmove', handleMouseMove);
        mouseDownRef.current = null;
      }
    }) as unknown as EventListener;

    const handleMouseUp = ((upEvent: MouseEvent | TouchEvent) => {
      if (!mouseDownRef.current) return;

      const upClientX =
        'touches' in upEvent ? upEvent.touches[0].clientX : upEvent.clientX;
      const deltaX = Math.abs(upClientX - mouseDownRef.current.x);
      const deltaTime = Date.now() - mouseDownRef.current.time;

      if (deltaX < CLICK_THRESHOLD && deltaTime < CLICK_TIME_THRESHOLD) {
        // It's a click
        if (rangeRef.current) {
          const rect = rangeRef.current.getBoundingClientRect();
          const clickPosition = ((upClientX - rect.left) / rect.width) * 100;
          const newValue =
            minValue + (clickPosition / 100) * (maxValue - minValue);
          const clampedValue = Math.min(Math.max(newValue, minValue), maxValue);
          onChange(clampedValue);
        }
      }

      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleMouseMove);
      window.removeEventListener('touchend', handleMouseUp);
      mouseDownRef.current = null;
    }) as unknown as EventListener;

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('touchmove', handleMouseMove);
    window.addEventListener('touchend', handleMouseUp);
  };

  useEffect(() => {
    if (rangeRef.current) {
      const valueRatio = (value - minValue) / (maxValue - minValue);
      const newThumbPos = valueRatio * 100;
      thumbRef.current?.style.setProperty('left', `${newThumbPos}%`);
    }
  }, [value, minValue, maxValue]);

  return (
    <div
      className="absolute bottom-0 top-0 cursor-grab active:cursor-grabbing"
      onMouseDown={handleRangeDown}
      onTouchStart={handleRangeDown}
      style={style}
    >
      <div
        className={border({ color, className: 'relative -mx-[2px] h-full' })}
      >
        <div
          className={border({
            color,
            className:
              'bg-gray-90/10 absolute left-[-3px] top-[-3px] h-[calc(100%+6px)] w-[calc(100%+6px)] rounded-lg',
          })}
        />
        <div
          ref={rangeRef}
          className="relative h-full"
          style={{
            width: `calc(100% - ${THUMB_WIDTH}px)`,
            pointerEvents: hideThumb ? 'none' : 'auto',
            opacity: hideThumb ? 0 : 1,
          }}
        >
          <ValueInfo open={isDraggingThumb} value={value} format={format}>
            <div
              className={`absolute my-[3px] h-[26px] cursor-ew-resize rounded-full  transition-colors ${
                color === 'violet' ? 'bg-white' : 'bg-gray-90'
              }`}
              ref={thumbRef}
              style={{
                left: `${getThumbPosition(value)}%`,
                width: `${THUMB_WIDTH}px`,
              }}
              onMouseDown={handleThumbMouseDown}
              onTouchStart={handleThumbTouchStart}
            />
          </ValueInfo>
        </div>
      </div>
    </div>
  );
};
